import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdesaoService } from '../../../../services/adesao/adesao.service';
import { AssinaturaCancelada, AssinaturaCanceladaPendente, BloqueioAdesao } from '../../../../interfaces/adesao.dto';
import { BrlPipe } from '../../../../utils/brl.pipe';
import { PendenciaSimComponent } from '../pendencia-sim/pendencia-sim.component';

/** Uma linha da lista, como no admin; `cobravel` é a parte que a Fila regulariza. */
export interface LinhaCancelada extends AssinaturaCancelada {
  cobravel?: AssinaturaCanceladaPendente;
  bloqueios: BloqueioAdesao[];
}

/**
 * Assinatura cancelada com fatura em aberto: a API do CartãoSiM recusa adesão nova. Lista todas as
 * canceladas como o admin, retraídas; a linha abre com as parcelas e, na que a Fila cobra, com a regularização.
 */
@Component({
  selector: 'app-pendencia-cancelada-sim',
  standalone: true,
  imports: [CommonModule, BrlPipe, PendenciaSimComponent],
  templateUrl: './pendencia-cancelada-sim.component.html',
  styleUrl: './pendencia-cancelada-sim.component.scss',
})
export class PendenciaCanceladaSimComponent implements OnChanges {
  @Input() personId = 0;
  @Input() clinicId = 0;
  @Input() cpf = '';
  @Input() guiche = '';
  @Input() procedimentos: { cdProcedimento: number; quantidade: number }[] = [];
  @Input() naPausa = false;
  @Output() temPendencia = new EventEmitter<boolean>();

  assinaturas: AssinaturaCanceladaPendente[] = [];
  linhas: LinhaCancelada[] = [];
  /** Linhas abertas; todas começam retraídas. */
  abertas = new Set<number>();
  /** Assinaturas cujo painel de regularização não achou nada a cobrar aqui. */
  semNada = new Set<number>();
  private carga = 0;

  constructor(private adesaoService: AdesaoService) {}

  /** Relê a lista sem recriar o card: recriar reemitiria temPendencia e giraria em loop. */
  porAssinatura = (_: number, l: { subscriptionId: number }) => l.subscriptionId;

  /** Sem nada a regularizar pode ser assinatura já quitada: relê a lista antes de mostrar o aviso. */
  async onTemPendencia(subscriptionId: number, tem: boolean): Promise<void> {
    if (tem) {
      this.semNada.delete(subscriptionId);
      return;
    }
    await this.carregar();
    if (this.assinaturas.some(a => a.subscriptionId === subscriptionId)) this.semNada.add(subscriptionId);
  }

  alternar(subscriptionId: number): void {
    if (this.abertas.has(subscriptionId)) this.abertas.delete(subscriptionId);
    else this.abertas.add(subscriptionId);
  }

  get totalAbertas(): number {
    return this.linhas.reduce((t, l) => t + l.faturasAbertas, 0);
  }

  /** Assinatura que ainda não reportou conta como regularizável (evita piscar para o aviso de ticket). */
  regularizavel(a: AssinaturaCanceladaPendente): boolean {
    return !a.soTicket && !this.semNada.has(a.subscriptionId);
  }

  rotulo(b: BloqueioAdesao): string {
    return b.tipo === 'multa' ? 'Multa de cancelamento' : b.tipo === 'anual' ? 'Anuidade' : 'Fatura';
  }

  /** Outras assinaturas com fatura em aberto ("o A (adesão ...) e o B"), com "(ticket)" nas que não se regularizam aqui. */
  outras(a: AssinaturaCanceladaPendente): string {
    const nomes = this.linhas
      .filter(l => l.subscriptionId !== a.subscriptionId && l.faturasAbertas > 0)
      .map(l => `o ${l.plano} (adesão ${this.data(l.adesao)})${l.cobravel && !this.regularizavel(l.cobravel) ? ' (ticket)' : ''}`);
    return nomes.length > 1 ? `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}` : nomes[0] ?? '';
  }

  async onRegularizado(): Promise<void> {
    await this.carregar();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['personId']) {
      this.abertas.clear();
      await this.carregar();
    }
  }

  async carregar(): Promise<void> {
    const n = ++this.carga;
    const [assinaturas, todas, bloqueios] = this.personId
      ? await Promise.all([
          this.adesaoService.pendenciaCancelada(this.personId),
          this.adesaoService.assinaturasCanceladas(this.personId),
          this.adesaoService.bloqueios(this.personId),
        ])
      : [[], [], []];
    if (n !== this.carga) return;
    this.assinaturas = assinaturas;
    this.linhas = this.montarLinhas(assinaturas, todas, bloqueios ?? []);
    this.semNada.clear();
    this.temPendencia.emit(assinaturas.length > 0);
  }

  /** Sem a lista completa (falha da rota), monta só com as que a Fila cobra. */
  private montarLinhas(cobraveis: AssinaturaCanceladaPendente[], todas: AssinaturaCancelada[] | null, bloqueios: BloqueioAdesao[]): LinhaCancelada[] {
    const base = todas ?? cobraveis.map(a => ({ subscriptionId: a.subscriptionId, plano: a.plano, adesao: a.adesao, faturasAbertas: a.faturasPendentes }));
    const linhas = base.map(t => ({
      ...t,
      cobravel: cobraveis.find(a => a.subscriptionId === t.subscriptionId),
      bloqueios: bloqueios.filter(b => b.subscriptionId === t.subscriptionId),
    }));
    for (const a of cobraveis)
      if (!linhas.some(l => l.subscriptionId === a.subscriptionId))
        linhas.push({ subscriptionId: a.subscriptionId, plano: a.plano, adesao: a.adesao, faturasAbertas: a.faturasPendentes, cobravel: a,
                      bloqueios: bloqueios.filter(b => b.subscriptionId === a.subscriptionId) });
    return linhas;
  }

  private data(iso: string): string {
    const [a, m, d] = (iso ?? '').slice(0, 10).split('-');
    return a && m && d ? `${d}/${m}/${a}` : '';
  }
}
